/**
 * Authentication Store (Zustand)
 * Manages authentication state and user session
 */

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { UserProfile } from "@/types/auth";
import { authApi } from "@/api/auth";
import { TokenStorage, refreshSession, type RefreshResult } from "@/api/client";
import { endLoginEverywhere, signOutDueAtStartup } from "@/utils/idleSession";
import { STORAGE_KEYS } from "@/constants";
import { roleHasPermission } from "@/utils/permissions";

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  /**
   * "Remember me" unticked and the server couldn't be reached when this tab
   * opened: the login may still be live (or someone else's, ended when the
   * browser closed). Pages show a "can't reach the server" screen -- never
   * the login form, which would look like a sign-out -- until a retry
   * settles it or the user signs this computer out. Not persisted.
   */
  serverUnreachable: boolean;
  retryConnection: () => Promise<RefreshResult>;
  signOutThisComputer: () => void;
  setUser: (user: UserProfile | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  logout: () => void;
  /**
   * Sign this tab out without touching the login the other tabs share (the
   * persisted state and the shared keys stay as they are, until this tab
   * signs in again or a real logout).
   */
  endInThisTab: () => void;
  hasRole: (roles: string | string[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

// Captured reference to the store's set() — used by onRehydrateStorage to
// properly update isHydrated via set() instead of direct mutation.
// Direct mutation (state.isHydrated = true) silently fails when state.logout()
// is called first, because logout() creates a new state object and the mutation
// applies to the discarded old one — leaving isHydrated permanently false.
let storeSet: ((partial: Partial<AuthState>) => void) | null = null;
let storeGet: (() => AuthState) | null = null;

// While true, this tab's auth state isn't written to the storage every tab
// shares (see endInThisTab). Any login or logout here writes again.
let persistPaused = false;
const sharedStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    if (persistPaused) return;
    try {
      localStorage.setItem(name, value);
    } catch {
      // Storage full or blocked: this tab stays signed in from memory; a
      // throw here would fail the login that called set().
    }
  },
  removeItem: (name: string) => localStorage.removeItem(name),
}));

// Bumped by every login in this tab: a boot refresh that settles after one
// must not apply its result to the new login.
let loginGeneration = 0;

// Shared by the actions and the boot below (which runs during create(),
// before useAuthStore exists, so it can't call the actions).
function signOutState(extra: Partial<AuthState> = {}): void {
  persistPaused = false;
  TokenStorage.clearTokens();
  storeSet?.({
    user: null,
    isAuthenticated: false,
    serverUnreachable: false,
    ...extra,
  });
}

function endInThisTabState(extra: Partial<AuthState> = {}): void {
  persistPaused = true;
  TokenStorage.forgetAccessToken();
  storeSet?.({
    user: null,
    isAuthenticated: false,
    serverUnreachable: false,
    ...extra,
  });
}

// The saved user is the one from sign-in, but each refresh resolves the
// login again (a parent in two families gets the family the server picks
// now): after a boot refresh, the saved user takes the server's profile, for
// the same login only -- not over a newer login another tab saved meanwhile.
// Silent on failure (no toast); the next boot tries again.
function refreshSavedUser(generation: number): void {
  Promise.resolve()
    .then(() => authApi.getProfile({ skipToast: true }))
    .then((response) => {
      const current = storeGet?.();
      if (generation !== loginGeneration || !current?.isAuthenticated) return;
      if (!current.user || response.data?.id !== current.user.id) return;
      if (sharedUserId() !== current.user.id) return;
      // A lookup that failed is an error (kept as saved, below); a profile
      // without a family is one: the parent was unlinked.
      storeSet?.({ user: { ...current.user, ...response.data } });
    })
    .catch(() => undefined);
}

/** The user id every tab shares (persisted auth state), or null. */
function sharedUserId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH_STORAGE);
    return raw ? (JSON.parse(raw)?.state?.user?.id ?? null) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      storeSet = set;
      storeGet = get;
      return {
        user: null,
        isAuthenticated: false,
        isHydrated: false,

        serverUnreachable: false,

        setUser: (user) => {
          persistPaused = false;
          loginGeneration++;
          set({ user, isAuthenticated: !!user, serverUnreachable: false });
        },

        retryConnection: async () => {
          const result = await refreshSession();
          // Signed out meanwhile (another tab, idle): nothing to settle, and a
          // set() here would persist this tab's signed-out state over a newer
          // login in another tab.
          if (!get().serverUnreachable || !get().isAuthenticated) return result;
          if (result === "ok") set({ serverUnreachable: false });
          else if (result === "refused") signOutState();
          return result;
        },

        // Ends the login for every tab on this computer, even offline: the
        // shared state is cleared (later page loads stay signed out) and the
        // revoke is tried; the cookie is a browser-session one either way.
        signOutThisComputer: () => {
          endLoginEverywhere({ note: false });
          signOutState();
        },

        setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

        logout: () => signOutState(),

        endInThisTab: () => endInThisTabState(),

        hasRole: (roles) => {
          const { user } = get();
          if (!user) return false;
          const roleArray = Array.isArray(roles) ? roles : [roles];
          return roleArray.includes(user.role);
        },

        hasPermission: (permission) => {
          return roleHasPermission(get().user?.role, permission);
        },
      };
    },
    {
      name: STORAGE_KEYS.AUTH_STORAGE,
      storage: sharedStorage,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // On rehydration the in-memory access token is always empty (a fresh page
      // load), so we can't decide auth state from it directly. Instead, for a
      // persisted-authenticated user, attempt a boot refresh against the
      // HttpOnly refresh cookie:
      //   - success  -> a new access token is minted in memory; stay logged in.
      //   - refused  -> cookie missing/expired; clear the persisted session.
      //   - failed   -> network blip, rate limit, 5xx: never clear the shared
      //                 state (that would sign every other tab out on its next
      //                 reload). Ticked: keep the session; the 401 path retries
      //                 the refresh on the first API call. Unticked: pages show
      //                 "can't reach the server" (serverUnreachable) -- on a
      //                 shared computer the login may be someone else's that
      //                 ended when the browser closed, so no name or data
      //                 shows, and no login form that would pass for a
      //                 sign-out while the login is still live.
      // Always tried, in every tab: "remember me" unticked is enforced by the
      // cookie itself (a browser-session cookie the browser drops on close)
      // and the idle sign-out. A new tab or a pasted link must open signed in,
      // and must never log out the tabs that are still signed in.
      // `isHydrated` is only set true once this resolves, so ProtectedRoute
      // doesn't flash the login page or a protected page prematurely.
      onRehydrateStorage: () => (state) => {
        const due = state?.isAuthenticated ? signOutDueAtStartup() : null;
        if (due === "idle") {
          // "Remember me" unticked and idle past the limit: don't refresh it
          // first. Revoked server-side too (a session cookie the browser
          // restored would otherwise stay live), and tabs still open on it
          // (asleep until now) sign out. No note on the login page: most
          // likely the browser was just closed, and the next person at the
          // computer shouldn't read about it.
          endLoginEverywhere({ note: false });
          signOutState({ isHydrated: true });
        } else if (due === "ended") {
          // From before the update: ends in this tab only, as a tab of the
          // old build may still be open on it.
          endInThisTabState({ isHydrated: true });
        } else if (state?.isAuthenticated) {
          const generation = loginGeneration;
          refreshSession()
            .then((result) => {
              if (generation !== loginGeneration) return; // someone signed in meanwhile
              if (!storeGet?.().isAuthenticated) return; // signed out meanwhile
              if (result === "refused") signOutState();
              else if (result === "ok") refreshSavedUser(generation);
              else if (result === "failed" && !TokenStorage.isRememberMe()) {
                storeSet?.({ serverUnreachable: true });
              }
            })
            .finally(() => storeSet?.({ isHydrated: true }));
        } else {
          storeSet?.({ isHydrated: true });
        }
      },
    },
  ),
);
